"use client"

import type React from "react"
import { motion, useReducedMotion } from "motion/react"

import { cn } from "@/lib/utils"
import { EASE_OUT_QUINT } from "@/lib/motion-presets"
import type { AnalysisMode as SharedAnalysisMode } from "@/lib/types/diagnosis"

type AnalysisMode = Exclude<SharedAnalysisMode, 'weed'>

interface HeroSectionProps {
  mode?: AnalysisMode
  className?: string
  children?: React.ReactNode
  /** Kept for API compatibility. The hero's entrance animation now lives on
   * the parent shell (single framer-motion fade+translate on the whole hero
   * block) so individual children stay static. */
  animate?: boolean
}

const HEADLINES: Record<AnalysisMode, { lead: string; accent: string; subtitle: string }> = {
  identify: {
    lead: "Identify your grass type",
    accent: "from a photo.",
    subtitle: "Free grass identifier. Snap a photo to learn your grass type, then get a plan to beat weeds and brown spots, timed to your soil and weather.",
  },
  diagnose: {
    lead: "Diagnose your",
    accent: "lawn problem.",
    subtitle: "Snap a photo. Get the likely cause with a confidence score and a step-by-step fix.",
  },
}

function AccentSquiggle() {
  const reduce = useReducedMotion()
  return (
    <svg
      className="absolute left-0 right-0 -bottom-2 w-full h-3"
      viewBox="0 0 200 12"
      preserveAspectRatio="none"
      aria-hidden="true"
    >
      <motion.path
        d="M2 7 C 50 2, 150 12, 198 5"
        stroke="currentColor"
        strokeWidth="3"
        strokeLinecap="round"
        fill="none"
        opacity="0.55"
        initial={reduce ? false : { pathLength: 0 }}
        animate={reduce ? undefined : { pathLength: 1 }}
        transition={{ duration: 0.55, ease: EASE_OUT_QUINT, delay: 0.15 }}
      />
    </svg>
  )
}

export function HeroSection({
  mode = 'identify',
  className,
  children,
}: HeroSectionProps) {
  const { lead, accent, subtitle } = HEADLINES[mode]

  return (
    <div className={cn("text-center mb-4 sm:mb-6", className)}>
      <h1 className="text-fluid-hero font-extrabold text-gray-900 mb-4 tracking-[-0.03em] leading-none text-balance">
        {lead}<br />
        <span className="relative inline-block text-green-600">
          {accent}
          <AccentSquiggle />
        </span>
      </h1>

      <p className="text-fluid-lg text-gray-500 mt-6 mb-4 max-w-2xl mx-auto leading-relaxed sm:mb-6">
        {subtitle}
      </p>

      {children}
    </div>
  )
}
